import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import ChartTooltip from "./ChartTooltip";
import { LoadPhase, SkeletonCard } from "../../../components/ui";
import { CHART_CARD_FOOTPRINT } from "./EarningsCard";
import { BRAND, BRAND_DARK, EMPTY_DISC, EMPTY_DISC_LIGHT } from "../chartTheme";

/** What a figure the backend has not counted shows in place of a number. */
const NO_FIGURE = "-";

/** `25.0` reads as "25%"; a rate that is not whole keeps one decimal. */
const formatRate = (rate: number) =>
  `${Number.isInteger(rate) ? rate : rate.toFixed(1)}%`;

interface TotalClientsCardProps {
  /**
   * Clients with a plan running — the converted share of the requests below,
   * and the slice the pie is drawn from. `null` where the backend sent no
   * figure, which shows a dash beside the label rather than a zero.
   */
  paidClients: number | null;
  /** Every payment request ever raised, which is the pie's whole. */
  totalRequests: number | null;
  /**
   * What share of those requests converted, as the backend worked it out —
   * `25.0` for a quarter. Its own figure rather than the two above divided
   * here: the backend counts requests this app never lists.
   */
  conversionRate: number | null;
  /**
   * Whether anyone has been asked for money yet. This is what puts the two
   * counts in words — "0 clients" rather than a bare 0 — for a workspace that
   * has not started. It is not what decides whether a pie is drawn: any split
   * that comes to nothing shows the disc instead, this one included. See
   * `showsDisc`.
   */
  empty?: boolean;
  /**
   * Where the dashboard call is. The card is not drawn until it answers — a
   * pie redrawn a moment after landing reads as the screen arriving twice — and
   * a wait long enough to notice shimmers the whole card.
   */
  phase?: LoadPhase;
}

/** One figure beside the pie: a colored rule, the count, and what it counts. */
const Legend: React.FC<{
  color: string;
  value: React.ReactNode;
  label: string;
  /** The change pill, where there is a change worth showing. */
  badge?: React.ReactNode;
}> = ({ color, value, label, badge }) => (
  <li className="flex items-start gap-2.5">
    <span
      className="mt-0.5 w-[3px] h-8 rounded-full flex-shrink-0"
      style={{ backgroundColor: color }}
    />
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className="text-[14px] font-medium text-ink">{value}</span>
        {badge}
      </div>
      <div className="text-[12px] text-ink-muted whitespace-nowrap">
        {label}
      </div>
    </div>
  </li>
);

const TotalClientsCard: React.FC<TotalClientsCardProps> = ({
  paidClients,
  totalRequests,
  conversionRate,
  empty = false,
  phase = "ready",
}) => {
  // Paid clients are a subset of the requests raised, so the pie splits the
  // requests into converted and not-yet-converted rather than plotting the two
  // headline numbers against each other. Clamped at zero: a backend counting
  // the two figures differently must not draw a negative slice.
  const paid = paidClients ?? 0;
  const segments = [
    { key: "paid", label: "Paid clients", value: paid, color: BRAND },
    {
      key: "unconverted",
      label: "Not converted",
      value: Math.max(0, (totalRequests ?? 0) - paid),
      color: BRAND_DARK,
    },
  ];

  // A pie needs something to divide. Zero paid against zero requests is not a
  // split of anything, and Recharts draws exactly that — a card with a hole
  // where the chart goes. It happens well after the first run, too: a
  // workspace that has taken money but has nobody on a plan right now counts
  // both of these at zero while the earnings card beside it still plots a
  // year. So the disc stands in whenever there is no split, not only on a
  // workspace that has not started.
  const showsDisc = empty || segments.every((segment) => segment.value <= 0);

  return phase !== "ready" ? (
    <SkeletonCard phase={phase} className={CHART_CARD_FOOTPRINT} />
  ) : (
    <section
      className={`rounded-md border border-line bg-white p-4 sm:p-5 3xl:p-7 ${CHART_CARD_FOOTPRINT}`}
    >
      <h2 className="text-[16px] 3xl:text-[19px] font-semibold text-ink">
        Total clients
      </h2>

      <div className="mt-3 flex items-center gap-4 sm:gap-3">
        <div className="w-[120px] h-[120px] sm:w-[140px] sm:h-[140px] xl:w-[160px] xl:h-[160px] 3xl:w-[190px] 3xl:h-[190px] flex-shrink-0">
          {showsDisc ? (
            // A disc rather than a chart: there is nothing to divide, so there
            // is no slice to point at and nothing for a tooltip to say. The
            // halves are tints of the brand rather than two categories — they
            // are always even, whatever the counts beside them say. It holds
            // the pie's own place so the card does not change shape the day
            // the first payment lands.
            <svg
              viewBox="0 0 100 100"
              className="w-full h-full"
              aria-hidden="true"
            >
              <circle cx="50" cy="50" r="50" fill={EMPTY_DISC_LIGHT} />
              <path d="M50,0 A50,50 0 0 1 50,100 Z" fill={EMPTY_DISC} />
            </svg>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip
                  content={({ active, payload }) => {
                    const point = payload?.[0]?.payload as
                      | (typeof segments)[number]
                      | undefined;
                    if (!point) return null;
                    return (
                      <ChartTooltip
                        active={active}
                        swatch={point.color}
                        value={String(point.value)}
                        name={point.label.toLowerCase()}
                      />
                    );
                  }}
                />
                <Pie
                  data={segments}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={0}
                  outerRadius="100%"
                  startAngle={90}
                  endAngle={-270}
                  // 2px surface gap keeps adjacent fills from bleeding together.
                  stroke="#FFFFFF"
                  strokeWidth={2}
                >
                  {segments.map((segment) => (
                    <Cell key={segment.key} fill={segment.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Direct labels double as the legend, so identity is never
            color-alone. Wherever the disc stands in, both rules take the one
            brand color: there are no slices for them to key to, so a second
            color would be pointing at a distinction the disc is not making. On
            a workspace that has not started the zeros also name what they are
            counting, since neither is a share of anything yet. */}
        <ul className="flex-1 min-w-0 space-y-4">
          <Legend
            color={BRAND}
            value={empty ? "0 clients" : paidClients ?? NO_FIGURE}
            label="Paid clients"
            badge={
              // Nothing to report where nothing was asked for — a conversion
              // rate over no requests is a share of nothing, whatever figure
              // the backend worked out for it — and nothing either where it
              // sent no rate at all. The pill says what it is on hover, since a
              // bare "25%" beside a count could be read as a change rather
              // than a share.
              showsDisc || conversionRate === null ? undefined : (
                <span
                  className="rounded-full bg-[#E6F7EF] px-2 py-0.5 text-[11px] font-medium text-[#12855B]"
                  title={`${formatRate(
                    conversionRate
                  )} of requests became clients`}
                >
                  {formatRate(conversionRate)}
                </span>
              )
            }
          />
          <Legend
            color={showsDisc ? BRAND : BRAND_DARK}
            value={empty ? "0 Enquiries" : totalRequests ?? NO_FIGURE}
            label="Total requests"
          />
        </ul>
      </div>
    </section>
  );
};

export default TotalClientsCard;
